// Shared Examiner Tips Cheat Sheet — public, printable, deeply indexable.
// Walks the subject content tree and emits an entry for every topic whose
// id is in EXAMINER_BANK. Each entry shows: why marks are lost / what
// examiners want / common traps / Grade-9 move.

import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import {
  XCircle,
  CheckCircle,
  Lightbulb,
  Trophy,
  Printer,
  ArrowRight,
  Sparkle,
  BookOpen,
  WarningCircle,
  MagnifyingGlass,
  ListChecks,
  GraduationCap,
  Target,
} from '@phosphor-icons/react';

import SEO from '../components/SEO';
import { EXAMINER_BANK } from '../data/examinerBank';
import { mathsSections } from '../data/mathsContent';
import { englishSections } from '../data/englishContent';
import {
  biologySections,
  chemistrySections,
  physicsSections,
} from '../data/scienceContent';
import { geographySections } from '../data/subjects/geography/content';

const SITE_URL = 'https://grade9ready.com';

const SUBJECT_COLLECTIONS = {
  maths: [
    {
      discipline: 'Maths',
      sections: Array.isArray(mathsSections) ? mathsSections : [],
      buildPath: (section, topic) => `/maths/${section.slug}/${topic.slug || ''}`,
    },
  ],

  english: [
    {
      discipline: 'English',
      sections: Array.isArray(englishSections) ? englishSections : [],
      buildPath: (section, topic) => `/english/${section.slug}/${topic.slug || ''}`,
    },
  ],

  science: [
    {
      discipline: 'Biology',
      sections: Array.isArray(biologySections) ? biologySections : [],
      buildPath: (section, topic) => `/science/biology/${section.slug}/${topic.slug || ''}`,
    },
    {
      discipline: 'Chemistry',
      sections: Array.isArray(chemistrySections) ? chemistrySections : [],
      buildPath: (section, topic) => `/science/chemistry/${section.slug}/${topic.slug || ''}`,
    },
    {
      discipline: 'Physics',
      sections: Array.isArray(physicsSections) ? physicsSections : [],
      buildPath: (section, topic) => `/science/physics/${section.slug}/${topic.slug || ''}`,
    },
  ],

  geography: [
    {
      discipline: 'Geography',
      sections: Array.isArray(geographySections) ? geographySections : [],
      buildPath: (section, topic) =>
        `/subjects/geography/${section.slug}/${topic.slug || ''}`,
    },
  ],
};

const DEFAULT_CONFIG = {
  subjectKey: 'maths',
  path: '/maths-examiner-tips',
  crumbLabel: 'Examiner tips',
  seoTitle: 'GCSE Examiner Tips Cheat Sheet | Grade9Ready',
  seoDescription:
    'Free printable GCSE examiner tips covering common mistakes, mark-winning habits, common traps and Grade 9 moves.',
  h1: 'GCSE Examiner Tips Cheat Sheet',
  heroKicker: 'Free GCSE revision guide',
  heroLede:
    'A free printable revision guide showing how marks are lost, what examiners want, the traps to avoid, and the higher-grade moves pupils can use in exam answers.',
  appHubPath: '/maths',
  appHubLabel: 'Maths',
  related: [],
};

const TIP_TYPES = [
  {
    key: 'whyMarksLost',
    title: 'Why marks are lost',
    icon: XCircle,
    accent: 'rose',
    testPrefix: 'tip-why-lost',
  },
  {
    key: 'whatExaminersWant',
    title: 'What examiners want',
    icon: CheckCircle,
    accent: 'emerald',
    testPrefix: 'tip-want',
  },
  {
    key: 'commonTraps',
    title: 'Common traps',
    icon: Lightbulb,
    accent: 'amber',
    testPrefix: 'tip-traps',
  },
  {
    key: 'gradeNineMove',
    title: 'Grade 9 move',
    icon: Trophy,
    accent: 'primary',
    testPrefix: 'tip-grade9',
  },
];

function cx(...classes) {
  return classes.filter(Boolean).join(' ');
}

function slugify(value = '') {
  return String(value)
    .toLowerCase()
    .trim()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function safeJsonLd(data) {
  return JSON.stringify(data).replace(/</g, '\\u003c');
}

function normaliseConfig(config = {}) {
  return {
    ...DEFAULT_CONFIG,
    ...config,
    related: Array.isArray(config.related) ? config.related : DEFAULT_CONFIG.related,
  };
}

function arrayify(value) {
  if (!value) return [];
  return Array.isArray(value) ? value.filter(Boolean) : [value];
}

function cleanPath(path) {
  return String(path || '').replace(/\/+$/, '') || '/';
}

function getTopicSummary(topic) {
  return (
    topic?.summary ||
    topic?.description ||
    topic?.explainer?.intro ||
    topic?.explainer?.title ||
    topic?.learn?.chunks?.[0]?.body ||
    ''
  );
}

function buildEntries(subjectKey) {
  const collections = SUBJECT_COLLECTIONS[subjectKey] || [];
  const groups = [];

  collections.forEach((collection) => {
    const sections = Array.isArray(collection.sections) ? collection.sections : [];

    sections.forEach((section) => {
      const topics = Array.isArray(section?.topics) ? section.topics : [];

      const matches = topics
        .filter((topic) => topic?.id && EXAMINER_BANK[topic.id])
        .map((topic) => ({
          id: topic.id,
          title: topic.title || 'Untitled topic',
          slug: topic.slug || topic.id,
          summary: getTopicSummary(topic),
          bank: EXAMINER_BANK[topic.id],
          topicPath: cleanPath(collection.buildPath(section, topic)),
          discipline: collection.discipline,
        }));

      if (matches.length > 0) {
        const fallbackSlug = slugify(
          `${collection.discipline}-${section?.title || groups.length + 1}`
        );

        groups.push({
          sectionTitle: section?.title || 'Untitled section',
          sectionSlug: section?.slug || fallbackSlug,
          discipline: collection.discipline,
          topics: matches,
        });
      }
    });
  });

  return groups;
}

function countTipItems(groups) {
  return groups.reduce((total, group) => {
    return (
      total +
      group.topics.reduce((topicTotal, topic) => {
        return (
          topicTotal +
          TIP_TYPES.reduce((tipTotal, tipType) => {
            return tipTotal + arrayify(topic.bank?.[tipType.key]).length;
          }, 0)
        );
      }, 0)
    );
  }, 0);
}

function buildArticleJsonLd({ pageConfig, pageUrl, groups, totalTopics }) {
  const articleBody = groups
    .flatMap((group) =>
      group.topics.map((topic) => {
        const tipText = TIP_TYPES.map((tipType) => {
          const items = arrayify(topic.bank?.[tipType.key]);
          return `${tipType.title}: ${items.join(' ')}`;
        }).join(' ');

        return `${group.discipline} ${group.sectionTitle} ${topic.title}. ${tipText}`;
      })
    )
    .join('\n\n')
    .slice(0, 4500);

  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: pageConfig.h1,
    description: pageConfig.seoDescription,
    articleBody,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': pageUrl,
    },
    author: {
      '@type': 'Organization',
      name: 'Grade9Ready',
      url: SITE_URL,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Grade9Ready',
      url: SITE_URL,
    },
    datePublished: '2026-02-01',
    dateModified: '2026-05-22',
    inLanguage: 'en-GB',
    isAccessibleForFree: true,
    educationalLevel: 'GCSE',
    learningResourceType: 'Revision guide',
    teaches: pageConfig.crumbLabel,
    numberOfItems: totalTopics,
    audience: {
      '@type': 'EducationalAudience',
      educationalRole: 'student',
    },
  };
}

function buildItemListJsonLd({ pageUrl, groups }) {
  const itemListElement = groups.flatMap((group) =>
    group.topics.map((topic) => ({
      '@type': 'ListItem',
      position: 0,
      name: `${group.discipline}: ${topic.title}`,
      url: `${SITE_URL}${topic.topicPath}`,
    }))
  );

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'GCSE examiner tips topics',
    itemListOrder: 'https://schema.org/ItemListOrderAscending',
    url: pageUrl,
    numberOfItems: itemListElement.length,
    itemListElement: itemListElement.map((item, index) => ({
      ...item,
      position: index + 1,
    })),
  };
}

function TipBlock({ icon: Icon, title, items, accent = 'primary', testid }) {
  const content = arrayify(items);
  if (content.length === 0) return null;

  const accentMap = {
    rose: 'text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-900/10 border-rose-200 dark:border-rose-800/60',
    emerald:
      'text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/10 border-emerald-200 dark:border-emerald-800/60',
    amber:
      'text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800/60',
    primary: 'text-primary bg-primary/5 border-primary/30 dark:border-primary/40',
  };

  return (
    <div
      className={cx(
        'rounded-xl p-4 border-2 print:p-2 print:border print:break-inside-avoid',
        accentMap[accent] || accentMap.primary
      )}
      data-testid={testid}
    >
      <div className="flex items-center gap-1.5 mb-2 text-[10px] font-black uppercase tracking-wide">
        <Icon size={12} weight="fill" />
        {title}
      </div>

      {content.length > 1 ? (
        <ul className="space-y-1 text-xs leading-relaxed">
          {content.map((item, index) => (
            <li key={`${title}-${index}`} className="flex gap-2">
              <span className="opacity-50" aria-hidden="true">
                •
              </span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs leading-relaxed">{content[0]}</p>
      )}
    </div>
  );
}

function StatCard({ value, label, icon: Icon }) {
  return (
    <div className="neo-card p-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-2xl font-black leading-none">{value}</div>
          <div className="text-xs font-bold text-muted-foreground mt-1">{label}</div>
        </div>

        {Icon && (
          <div className="w-9 h-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
            <Icon size={18} weight="fill" />
          </div>
        )}
      </div>
    </div>
  );
}

function PrintButton({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="cheatsheet-print-btn"
      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-primary text-primary-foreground font-heading font-bold shadow-[3px_3px_0px_0px_rgb(var(--9ready-shadow))] hover:translate-y-[1px] transition-transform"
    >
      <Printer size={16} weight="bold" />
      Print or save as PDF
    </button>
  );
}

function Breadcrumbs({ pageConfig }) {
  return (
    <nav
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 text-xs text-muted-foreground print:hidden"
      aria-label="Breadcrumb"
    >
      <Link to="/" className="hover:text-foreground transition-colors">
        Home
      </Link>
      <span className="mx-2">/</span>
      <Link to="/free-revision-planner" className="hover:text-foreground transition-colors">
        Free GCSE revision tools
      </Link>
      <span className="mx-2">/</span>
      <span className="text-foreground font-bold">{pageConfig.crumbLabel}</span>
    </nav>
  );
}

function ContentsBox({ groups, pageConfig }) {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6 print:hidden">
      <details className="neo-card p-5">
        <summary className="font-heading font-bold cursor-pointer">
          In this cheat sheet
        </summary>

        <ol className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1 text-sm text-muted-foreground list-decimal list-inside">
          {groups.map((group) => (
            <li key={`${group.discipline}-${group.sectionSlug}`}>
              <a
                href={`#${group.sectionSlug}`}
                className="hover:text-foreground transition-colors"
              >
                {group.discipline !== pageConfig.appHubLabel ? `${group.discipline}: ` : ''}
                {group.sectionTitle}{' '}
                <span className="text-xs opacity-60">({group.topics.length})</span>
              </a>
            </li>
          ))}
        </ol>
      </details>
    </section>
  );
}

function EmptyTipsState({ pageConfig }) {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
      <div className="neo-card p-6 flex items-start gap-3">
        <WarningCircle size={24} weight="fill" className="text-amber-500 shrink-0" />
        <div>
          <h2 className="font-heading font-bold text-xl mb-1">
            No examiner tips are live yet
          </h2>
          <p className="text-sm text-muted-foreground leading-relaxed mb-4">
            This page is connected correctly, but no topics for this subject currently match
            entries in the examiner bank. Add matching topic IDs to EXAMINER_BANK to populate it.
          </p>

          <Link
            to={pageConfig.appHubPath}
            className="inline-flex items-center gap-2 text-sm font-bold text-primary hover:underline"
          >
            Explore {pageConfig.appHubLabel}
            <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
      </div>
    </section>
  );
}

function TopicArticle({ topic }) {
  return (
    <article
      className="neo-card p-5 print:p-3 print:border print:shadow-none print:break-inside-avoid"
      data-testid={`cheatsheet-topic-${topic.id}`}
    >
      <div className="flex items-start justify-between gap-4 mb-3 print:mb-1.5">
        <div>
          <h3 className="font-heading font-bold text-lg flex items-center gap-2 print:text-base">
            <BookOpen size={16} weight="fill" className="text-primary print:hidden" />
            {topic.title}
          </h3>

          {topic.summary && (
            <p className="mt-1 text-xs text-muted-foreground leading-relaxed line-clamp-2 print:hidden">
              {topic.summary}
            </p>
          )}
        </div>

        <Link
          to={topic.topicPath}
          className="hidden sm:inline-flex items-center gap-1 text-xs font-bold text-primary hover:underline print:hidden"
        >
          Practise topic
          <ArrowRight size={12} weight="bold" />
        </Link>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        {TIP_TYPES.map((tipType) => (
          <TipBlock
            key={`${topic.id}-${tipType.key}`}
            icon={tipType.icon}
            title={tipType.title}
            items={topic.bank?.[tipType.key]}
            accent={tipType.accent}
            testid={`${tipType.testPrefix}-${topic.id}`}
          />
        ))}
      </div>
    </article>
  );
}

function FinalCta({ pageConfig }) {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10 print:hidden">
      <div className="neo-card p-6 sm:p-8 bg-gradient-to-br from-primary/5 to-primary/10 border-primary/30">
        <h3 className="font-heading font-black text-2xl mb-2">
          Want every topic, every mark scheme and every AI-marked answer?
        </h3>

        <p className="text-muted-foreground mb-4 max-w-2xl leading-relaxed">
          This free cheat sheet gives pupils the headline examiner advice. Inside Grade9Ready,
          pupils can practise the topics, submit answers, get instant feedback and build a clearer
          revision plan before mocks and GCSE exams.
        </p>

        <div className="flex flex-wrap gap-3">
          <Link
            to="/signup"
            data-testid="cheatsheet-cta-final"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-primary text-primary-foreground font-heading font-bold shadow-[3px_3px_0px_0px_rgb(var(--9ready-shadow))]"
          >
            Create Parent Account
            <ArrowRight size={16} weight="bold" />
          </Link>

          <Link
            to={pageConfig.appHubPath}
            data-testid="cheatsheet-cta-explore"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl border-2 border-foreground/15 font-heading font-bold hover:border-foreground/40 transition-colors"
          >
            Explore {pageConfig.appHubLabel}
          </Link>

          <Link
            to="/free-revision-planner"
            data-testid="cheatsheet-cta-planner"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl border-2 border-foreground/15 font-heading font-bold hover:border-foreground/40 transition-colors"
          >
            Build a free revision plan
          </Link>
        </div>
      </div>
    </section>
  );
}

function RelatedTools({ related }) {
  if (!Array.isArray(related) || related.length === 0) return null;

  return (
    <section
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 print:hidden"
      data-testid="cheatsheet-related"
    >
      <h3 className="font-heading font-bold text-lg mb-3">
        Related free GCSE revision tools
      </h3>

      <ul className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
        {related.map((item) => (
          <li key={item.href}>
            <Link
              to={item.href}
              className="block neo-card p-4 hover:-translate-y-0.5 transition-all"
              data-testid={`cheatsheet-related-${
                item.testid || item.href.replace(/\W+/g, '-')
              }`}
            >
              <div className="text-[10px] font-black uppercase tracking-wide text-muted-foreground mb-1">
                {item.kicker}
              </div>

              <div className="font-heading font-bold text-sm leading-snug">
                {item.title}
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function ExaminerCheatSheet({ config }) {
  const pageConfig = useMemo(() => normaliseConfig(config), [config]);

  const groups = useMemo(
    () => buildEntries(pageConfig.subjectKey),
    [pageConfig.subjectKey]
  );

  const totalTopics = useMemo(
    () => groups.reduce((acc, group) => acc + group.topics.length, 0),
    [groups]
  );

  const totalSections = groups.length;

  const totalTipItems = useMemo(() => countTipItems(groups), [groups]);

  const pageUrl = `${SITE_URL}${pageConfig.path}`;

  const breadcrumbJsonLd = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: `${SITE_URL}/`,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Free GCSE revision tools',
          item: `${SITE_URL}/free-revision-planner`,
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: pageConfig.crumbLabel,
          item: pageUrl,
        },
      ],
    }),
    [pageConfig.crumbLabel, pageUrl]
  );

  const articleJsonLd = useMemo(
    () =>
      buildArticleJsonLd({
        pageConfig,
        pageUrl,
        groups,
        totalTopics,
      }),
    [pageConfig, pageUrl, groups, totalTopics]
  );

  const itemListJsonLd = useMemo(
    () =>
      buildItemListJsonLd({
        pageUrl,
        groups,
      }),
    [pageUrl, groups]
  );

  const handlePrint = () => {
    if (typeof window !== 'undefined') {
      window.print();
    }
  };

  return (
    <main
      className="min-h-screen bg-background"
      data-testid={`cheatsheet-${pageConfig.subjectKey}`}
    >
      <SEO
        title={pageConfig.seoTitle}
        description={pageConfig.seoDescription}
        path={pageConfig.path}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: safeJsonLd(breadcrumbJsonLd) }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: safeJsonLd(articleJsonLd) }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: safeJsonLd(itemListJsonLd) }}
      />

      <Breadcrumbs pageConfig={pageConfig} />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-10 print:pt-2 print:pb-3">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-fuchsia-100 text-fuchsia-700 border border-fuchsia-200 text-[11px] font-black uppercase tracking-wide mb-5 print:hidden">
          <Sparkle size={12} weight="fill" />
          Free · printable · {totalTopics} examiner tip topics
        </div>

        <div className="max-w-4xl">
          <div className="text-xs font-black uppercase tracking-wider text-primary mb-2 print:hidden">
            {pageConfig.heroKicker}
          </div>

          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-heading font-black leading-[1.05] mb-4 print:text-3xl"
            data-testid="cheatsheet-h1"
          >
            {pageConfig.h1}
          </h1>

          <p className="text-lg text-muted-foreground leading-relaxed mb-6 print:text-sm print:mb-3">
            {pageConfig.heroLede}
          </p>
        </div>

        <div className="grid sm:grid-cols-4 gap-3 max-w-4xl mb-6 print:hidden">
          <StatCard value={totalTopics} label="examiner tip topics" icon={BookOpen} />

          <StatCard value={totalSections} label="revision sections" icon={ListChecks} />

          <StatCard value={totalTipItems} label="individual tips" icon={Target} />

          <StatCard value="4" label="mark-winning views" icon={GraduationCap} />
        </div>

        <div className="flex flex-wrap gap-3 print:hidden">
          <PrintButton onClick={handlePrint} />

          <Link
            to="/signup"
            data-testid="cheatsheet-cta-trial"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl border-2 border-foreground/15 font-heading font-bold hover:border-foreground/40 transition-colors"
          >
            Get this inside the platform
            <ArrowRight size={16} weight="bold" />
          </Link>

          <a
            href="#cheatsheet-content"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl border-2 border-foreground/15 font-heading font-bold hover:border-foreground/40 transition-colors"
          >
            Jump to tips
            <MagnifyingGlass size={16} weight="bold" />
          </a>
        </div>

        <p className="text-xs text-muted-foreground mt-3 print:hidden">
          No signup needed for this page · A4 print-ready · designed for quick revision before
          tests and mocks.
        </p>
      </section>

      {groups.length > 0 ? (
        <>
          <ContentsBox groups={groups} pageConfig={pageConfig} />

          <section
            id="cheatsheet-content"
            className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 print:px-0 print:pb-3"
            data-testid="cheatsheet-content"
          >
            {groups.map((group) => (
              <section
                key={`${group.discipline}-${group.sectionSlug}`}
                id={group.sectionSlug}
                className="mb-10 scroll-mt-8 print:mb-4 print:break-inside-avoid"
              >
                <div className="mb-4 pb-2 border-b-2 border-foreground/15 print:mb-2">
                  <div className="text-[10px] font-black uppercase tracking-wide text-muted-foreground mb-1 print:hidden">
                    {group.discipline}
                  </div>

                  <h2 className="text-2xl sm:text-3xl font-heading font-black print:text-lg">
                    {group.sectionTitle}
                  </h2>

                  <p className="text-sm text-muted-foreground mt-1 print:hidden">
                    {group.topics.length}{' '}
                    {group.topics.length === 1 ? 'topic' : 'topics'} with
                    examiner-focused revision advice.
                  </p>
                </div>

                <div className="space-y-5 print:space-y-2">
                  {group.topics.map((topic) => (
                    <TopicArticle key={topic.id} topic={topic} />
                  ))}
                </div>
              </section>
            ))}
          </section>
        </>
      ) : (
        <EmptyTipsState pageConfig={pageConfig} />
      )}

      <FinalCta pageConfig={pageConfig} />

      <RelatedTools related={pageConfig.related} />

      <div className="hidden print:block px-6 pt-2 pb-4 text-[10px] text-muted-foreground border-t border-foreground/20">
        <strong>Grade9Ready</strong> · grade9ready.com · Free GCSE examiner tip cheat sheet ·{' '}
        {pageConfig.crumbLabel}
      </div>

      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 10mm;
          }

          html,
          body {
            background: #ffffff !important;
          }

          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          a {
            color: inherit !important;
            text-decoration: none !important;
          }

          .neo-card {
            box-shadow: none !important;
          }

          .line-clamp-2 {
            display: block !important;
            overflow: visible !important;
          }
        }
      `}</style>
    </main>
  );
}